import type { ReactNode } from 'react'
import { AnimatedNumber } from './AnimatedNumber'
import { cn } from '@/lib/utils'

interface DashboardHeroProps {
  /** Tiny eyebrow above the headline (e.g. "Today · Live") */
  eyebrow?: string
  /** The big headline number — rendered enormous, thin-weight */
  headlineValue: number
  /** Format the headline number — e.g. (n) => `${n}` */
  headlineFormat?: (n: number) => string
  /** Word(s) under the headline (e.g. "calls handled by your AI") */
  headlineLabel: string
  /** Optional supporting metric line (e.g. "47 minutes · 14 booked") */
  supporting?: ReactNode
  /** Right-side action cluster (filters, time range select) */
  actions?: ReactNode
  /** Activity items for the kinetic ticker. Optional — ticker hidden if empty. */
  ticker?: TickerItem[]
  /** Connection state — drives the orb's "alive" intensity */
  status?: 'live' | 'quiet' | 'offline'
  /** Real-time activity count (live chats + live calls). When > 0 the orb
   *  intensifies and a "X live now" label appears — turns the orb from
   *  decoration into a real signal. */
  liveActivity?: number
  className?: string
}

export interface TickerItem {
  /** Short label, e.g. "Sarah Lopez · Personal Injury" */
  label: string
  /** Optional secondary text shown after a · */
  meta?: string
  /** Tone tints the leading dot color */
  tone?: 'royal' | 'periwinkle' | 'green' | 'amber' | 'rose' | 'violet' | 'neutral'
  /** Optional internal href — when set, the chip becomes clickable */
  href?: string
}

const TONE_DOT: Record<NonNullable<TickerItem['tone']>, string> = {
  royal:      'bg-brand-royal',
  periwinkle: 'bg-brand-periwinkle',
  green:      'bg-emerald-500',
  amber:      'bg-amber-500',
  rose:       'bg-rose-500',
  violet:     'bg-violet-500',
  neutral:    'bg-muted-foreground/60',
}

/**
 * The bridge of the operation. Massive editorial typography, a breathing
 * AI-presence orb, and an infinite kinetic ticker of recent firm activity.
 *
 * Sits at the top of the Dashboard route. Uses .aurora-mesh on its parent
 * to get the slow-drifting brand gradient backdrop.
 */
export function DashboardHero({
  eyebrow,
  headlineValue,
  headlineFormat,
  headlineLabel,
  supporting,
  actions,
  ticker = [],
  status = 'live',
  liveActivity = 0,
  className,
}: DashboardHeroProps) {
  // Duplicate the ticker so the marquee loops seamlessly
  const tickerLoop = ticker.length > 0 ? [...ticker, ...ticker] : []

  return (
    <div
      className={cn(
        // Solid bg, no backdrop-filter. Stacked over the aurora-mesh layer
        // it caused per-frame re-blur on scroll — the "magnetic wheel" feel.
        'relative overflow-hidden rounded-3xl border bg-card shadow-tinted-lg mb-6 sm:mb-8',
        className,
      )}
    >
      {/* Glassmorphism inner highlight (taste rule: "real refraction") */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-3xl"
        style={{ boxShadow: 'inset 0 1px 0 hsl(0 0% 100% / 0.08), inset 0 -1px 0 hsl(0 0% 0% / 0.04)' }}
      />

      <div className="relative grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-5 lg:gap-10 px-6 sm:px-10 pt-7 pb-5 sm:pt-9 sm:pb-7">
        {/* === LEFT — Editorial headline === */}
        <div className="min-w-0 flex flex-col justify-center">
          {eyebrow && (
            <div className="flex items-center gap-2 mb-3 sm:mb-4">
              {status === 'live' && (
                <span className="relative inline-flex h-2 w-2 shrink-0">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-50 animate-breath" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
              )}
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
                {eyebrow}
              </p>
            </div>
          )}

          {/* Massive thin-weight number — taste rule: hierarchy via weight, not just scale */}
          <div className="font-heading font-extralight tracking-[-0.06em] leading-[0.85] text-foreground">
            <span className="text-[4.5rem] sm:text-[6rem] lg:text-[7.5rem] tabular-nums">
              <AnimatedNumber value={headlineValue} format={headlineFormat} duration={1100} />
            </span>
          </div>

          <p className="text-base sm:text-lg text-muted-foreground mt-4 sm:mt-5 max-w-md leading-snug">
            {headlineLabel}
          </p>

          {supporting && (
            <div className="mt-4 sm:mt-5 text-sm text-foreground/80">{supporting}</div>
          )}

          {actions && (
            <div className="mt-6 sm:mt-8 flex flex-wrap items-center gap-2">{actions}</div>
          )}
        </div>

        {/* === RIGHT — The Pulse === */}
        <div className="relative flex items-center justify-center min-h-[220px] sm:min-h-[280px] lg:min-h-[320px]">
          <Pulse status={status} liveActivity={liveActivity} />
        </div>
      </div>

      {/* === KINETIC STRIP — today's appointments (clickable) or fallback
           insights when nothing's scheduled. Pauses on hover so users can
           actually read and click. Hidden on mobile: cramped width turns
           it into snail-paced chrome that doesn't earn the scroll-perf cost. */}
      {tickerLoop.length > 0 && (
        <div className="group relative border-t border-border/40 bg-background/40 hidden sm:block">
          <div className="overflow-hidden py-3">
            <div className="flex items-center gap-8 animate-ticker whitespace-nowrap will-change-transform group-hover:[animation-play-state:paused]">
              {tickerLoop.map((item, i) => {
                const dot = (
                  <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', TONE_DOT[item.tone || 'royal'])} />
                )
                const inner = (
                  <>
                    {dot}
                    <span className="text-foreground font-medium">{item.label}</span>
                    {item.meta && (
                      <span className="text-muted-foreground/70">· {item.meta}</span>
                    )}
                  </>
                )
                return item.href ? (
                  <a
                    key={i}
                    href={item.href}
                    className="flex items-center gap-2.5 text-xs hover:text-brand-royal transition-colors cursor-pointer"
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={i} className="flex items-center gap-2.5 text-xs">
                    {inner}
                  </div>
                )
              })}
            </div>
          </div>
          {/* Edge fades for seamless infinite-scroll illusion */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-card to-transparent" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-card to-transparent" />
        </div>
      )}
    </div>
  )
}

/* The Pulse — abstract AI presence. Layered radial gradients with staggered
   pulse animations. Pure CSS, no canvas/WebGL, ~3KB visual cost.

   When liveActivity > 0 the orb intensifies (faster rings, brighter core,
   color-shifted toward emerald) — turns it from decoration into signal. */
function Pulse({ status, liveActivity }: { status: 'live' | 'quiet' | 'offline'; liveActivity: number }) {
  const baseIntensity = status === 'live' ? 1 : status === 'quiet' ? 0.5 : 0.2
  const isActive = status !== 'offline'
  const isHot = liveActivity > 0
  // Activity boost — caps at 5+ live conversations so a busy firm doesn't
  // make the orb fluorescent. Layered on top of the base status intensity.
  const activityBoost = isHot ? Math.min(0.4, liveActivity * 0.08) : 0
  const intensity = Math.min(1.4, baseIntensity + activityBoost)
  // Speed scales with activity — idle is meditative, busy is energetic.
  const ringDuration = isHot ? 2.6 : 4.2
  const breathDuration = isHot ? 1.6 : 2.4
  // Hot state shifts toward emerald (matching the eyebrow's "alive" dot)
  // so two visual signals reinforce instead of compete.
  const accentHsl = isHot ? '142 71% 45%' : 'var(--grad-periwinkle)'

  return (
    /* Simplified orb: ONE main element + 1-2 soft halo rings. No filter:blur
       (created a rectangular bounding-box halo around the rounded shape — the
       "square around the orb" James saw). No backdrop-filter (per-frame
       re-blur cost). No will-change hints (over-promotion creates more
       compositor layers than it saves). The radial gradient on the main orb
       does ALL the depth work. */
    <div className="relative w-[96px] h-[96px] sm:w-[112px] sm:h-[112px] lg:w-[128px] lg:h-[128px] flex items-center justify-center">
      {/* Two soft halo rings — radial gradients fading to fully transparent
          at the edges, so there's NO rectangular bounding box visible. */}
      {isActive && [0, 1].map(i => (
        <div
          key={i}
          aria-hidden="true"
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background: `radial-gradient(circle, hsl(${accentHsl} / ${0.22 * intensity}) 28%, transparent 68%)`,
            animation: `pulseRingExpand ${ringDuration}s var(--ease-out, cubic-bezier(0.23,1,0.32,1)) infinite`,
            animationDelay: `${i * (ringDuration / 2)}s`,
          }}
        />
      ))}

      {/* The orb itself — single element, single radial gradient does all
          the depth. Specular highlight at 36%/30%, accent ring, royal mid,
          violet outer fading to transparent. boxShadow provides the outer
          glow. Breath animation is the only continuous cost. */}
      <div
        aria-hidden="true"
        className="relative w-[58%] h-[58%] rounded-full"
        style={{
          background: `radial-gradient(circle at 36% 30%,
            hsl(0 0% 100% / 0.95) 0%,
            hsl(${accentHsl} / 0.92) 14%,
            hsl(${accentHsl} / 0.65) 32%,
            hsl(var(--grad-royal) / 0.50) 56%,
            hsl(var(--grad-violet) / 0.30) 80%,
            transparent 100%)`,
          boxShadow: `
            inset 0 -8px 20px hsl(0 0% 0% / 0.20),
            inset 0 4px 14px hsl(0 0% 100% / 0.08),
            0 0 48px hsl(${accentHsl} / ${0.50 * intensity}),
            0 0 96px hsl(var(--grad-royal) / ${0.28 * intensity})
          `,
          animation: isActive ? `pulseCoreBreath ${breathDuration}s var(--ease-in-out, ease-in-out) infinite` : undefined,
        }}
      />

      {/* Status label — sits below the orb. Swaps to a live count when
          activity is non-zero, so the orb earns its real estate. */}
      <div className="absolute -bottom-6 left-0 right-0 text-center">
        {isHot ? (
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-500 whitespace-nowrap">
            {liveActivity} live now
          </p>
        ) : (
          <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-muted-foreground whitespace-nowrap">
            {status === 'live' ? 'AI · Ready' : status === 'quiet' ? 'AI · Standing by' : 'AI · Offline'}
          </p>
        )}
      </div>
    </div>
  )
}
